import {
  Ban,
  CircleDashed,
  Lock,
  RefreshCw,
  Activity,
  Unplug,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { WsStatus } from "../../api/ws";
import type {
  MarketStatusSnapshot,
  PriceTick,
  StreamHealthSnapshot,
} from "../../hooks/useWsConnection";
import { cn } from "@/lib/utils";
import { useFlashOnChange } from "../../hooks/useFlashOnChange";

/**
 * The live-price tile (SPEC §12 §14 §25). One explicit six-state tile: a closed
 * market and a broken stream must NOT look identical — each state pairs a distinct
 * colour + Lucide icon + tile shape. Extracted from the former dashboard TopBar so
 * the unified {@link AppHeader} can show it on every page.
 */
export type PriceTileState =
  | "live"
  | "awaiting"
  | "market-closed"
  | "preempted"
  | "reconnecting"
  | "disconnected";

/** Per-state §25 presentation: icon, name (for data-icon + a11y), colour + shape. */
const TILE_META: Record<
  PriceTileState,
  { Icon: LucideIcon; name: string; spin?: boolean; tile: string; icon: string }
> = {
  live: {
    Icon: Activity,
    name: "live",
    tile: "border-buy/40 bg-buy-soft",
    icon: "text-buy",
  },
  awaiting: {
    Icon: CircleDashed,
    name: "awaiting",
    spin: true,
    tile: "border-info/40 border-dashed bg-info-soft",
    icon: "text-info",
  },
  "market-closed": {
    Icon: Lock,
    name: "market-closed",
    tile: "border-border-strong border-dashed bg-surface-2",
    icon: "text-muted-foreground",
  },
  preempted: {
    Icon: Ban,
    name: "preempted",
    tile: "border-warning/50 border-dotted bg-warning-soft",
    icon: "text-warning",
  },
  reconnecting: {
    Icon: RefreshCw,
    name: "reconnecting",
    spin: true,
    tile: "border-warning/40 bg-warning-soft",
    icon: "text-warning",
  },
  disconnected: {
    Icon: Unplug,
    name: "disconnected",
    tile: "border-sell/50 bg-sell-soft",
    icon: "text-sell",
  },
};

interface PriceTileProps {
  price: PriceTick | null;
  wsStatus: WsStatus;
  marketStatus?: MarketStatusSnapshot | null;
  streamHealth?: StreamHealthSnapshot | null;
  className?: string;
}

export default function PriceTile({
  price,
  wsStatus,
  marketStatus = null,
  streamHealth = null,
  className,
}: PriceTileProps) {
  const state = priceTileState(price, wsStatus, marketStatus, streamHealth?.state ?? null);
  const caption = captionFor(state);
  const meta = TILE_META[state];
  const displayValue = price != null ? price.mid.toFixed(2) : "—";
  // Brief buy/sell tint on a mid move — flash only, never a number roll.
  const flash = useFlashOnChange(price?.mid);
  const ariaLabel =
    state === "live"
      ? `Prix live ${displayValue}`
      : `Prix live indisponible — ${caption}`;

  return (
    <div
      className={cn(
        "top-bar__price flex items-center gap-2 rounded-xl border px-3.5 py-1.5 shadow-[var(--shadow-elevate)] backdrop-blur-sm transition-shadow",
        `top-bar__price--${state}`,
        meta.tile,
        className,
      )}
      data-state={state}
      aria-label={ariaLabel}
      aria-live="polite"
    >
      <span
        className={cn("top-bar__price-icon flex items-center", meta.icon)}
        data-icon={meta.name}
        aria-hidden
      >
        <meta.Icon className={cn("size-4", meta.spin && "animate-spin")} />
      </span>
      <span
        className="top-bar__price-value text-foreground rounded-sm font-mono text-xl font-bold tabular-nums tracking-tight"
        data-flash={flash ?? undefined}
      >
        {displayValue}
      </span>
      {caption && (
        <span
          className="top-bar__price-caption text-muted-foreground text-xs"
          data-testid="price-caption"
        >
          {caption}
        </span>
      )}
    </div>
  );
}

export function priceTileState(
  price: PriceTick | null,
  wsStatus: WsStatus,
  marketStatus: MarketStatusSnapshot | null,
  streamHealthState: string | null = null,
): PriceTileState {
  // 1. WS-level failure dominates: if the WS isn't open, we don't have a
  //    trustworthy view of the backend's market_status at all.
  if (wsStatus === "connecting" || wsStatus === "reconnecting") {
    return "reconnecting";
  }
  if (wsStatus === "closed" || wsStatus === "auth-failed" || wsStatus === "idle") {
    return "disconnected";
  }
  // 2. WS open. The backend's market-status signal wins over absence of ticks.
  if (marketStatus && !marketStatus.open) {
    return "market-closed";
  }
  // 3. WS open, market open, but the backend says the feed is preempted.
  if (streamHealthState === "STREAM_PREEMPTED") {
    return "preempted";
  }
  // 4. WS open, market open (or unknown), and we have a fresh price.
  if (price != null) {
    return "live";
  }
  // 5. WS open, market open or unknown, no tick yet.
  return "awaiting";
}

function captionFor(state: PriceTileState): string | null {
  switch (state) {
    case "live":
      return null;
    case "awaiting":
      return "attente de tick";
    case "market-closed":
      return "Marché fermé";
    case "preempted":
      return "flux préempté — compte IG utilisé ailleurs ?";
    case "reconnecting":
      return "reconnexion…";
    case "disconnected":
      return "Flux déconnecté";
  }
}
